import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { 
  LogOut, MapPin, Camera, Clock, Check, Phone,
  ChevronRight, Loader2, ArrowLeft, Upload, Users, Navigation, Power, PowerOff, GraduationCap
} from "lucide-react";
import { logger } from "../utils/logger";
import OpsLogin from "../components/ops/OpsLogin";
import BookingCard from "../components/ops/BookingCard";
import OpsAttendanceCard from "../components/ops/OpsAttendanceCard";
import useOpsTracker from "../hooks/useOpsTracker";
import TrainingTab from "../components/admin/TrainingTab";
import StaffAssistantWidget from "../components/StaffAssistantWidget";
import InternalChatWidget from "../components/InternalChatWidget";
import PolicyResignBanner from "../components/PolicyResignBanner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OpsPortal = () => {
  const [opsAuth, setOpsAuth] = useState(() => {
    // Metadata only (no raw JWT); real auth = HttpOnly cookie
    const raw = sessionStorage.getItem("doozygo_ops_meta");
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
  });

  const handleLogout = async () => {
    try { await fetch(`${API_URL}/api/ops/logout`, { method: 'POST', credentials: 'include' }); }
    catch (err) { logger.warn('[Ops] Logout error:', err?.message); }
    sessionStorage.removeItem("doozygo_ops_meta");
    setOpsAuth(null);
  };

  if (!opsAuth) return <OpsLogin onLogin={setOpsAuth} />;
  return <OpsDashboard auth={opsAuth} onLogout={handleLogout} />;
};

const OpsDashboard = ({ auth, onLogout }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [showTeam, setShowTeam] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [showAssign, setShowAssign] = useState(null);
  const [newMember, setNewMember] = useState({ name: "", phone: "", pin: "" });
  const [showAddMember, setShowAddMember] = useState(false);
  const [showTraining, setShowTraining] = useState(false);
  const isManager = auth.role === "manager" || auth.role === "ops_manager";

  // Live GPS tracking (extracted to reusable hook)
  const tracker = useOpsTracker(auth.phone);
  const { trackingActive, trackingStatus, setTrackingStatus, getLocation, geoError, setGeoError } = tracker;
  const startTracking = tracker.start;
  const stopTracking = tracker.stop;

  // Cookie (via withCredentials) is primary; X-Ops-Auth header is fallback when cookies don't flow (prod edge cases).
  const authAxios = useMemo(() => axios.create({
    withCredentials: true,
    headers: auth?.token ? { 'X-Ops-Auth': auth.token } : {},
  }), [auth?.token]);

  const fetchBookings = useCallback(async () => {
    try {
      const res = await authAxios.get(`${API_URL}/api/ops/bookings/all`);
      setBookings(res.data);
    } catch (err) { logger.error(err); }
    setLoading(false);
    // API_URL and logger are module-level imports (stable); setBookings/setLoading
    // are React setters (stable). Only authAxios is a reactive dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authAxios]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  // Attendance heartbeat — ping ops endpoint every 5 min while portal is open
  useEffect(() => {
    const ping = () => { authAxios.post(`${API_URL}/api/ops/attendance/heartbeat`, {}).catch(() => {}); };
    ping();
    const id = setInterval(ping, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [authAxios]);

  const fetchTeam = useCallback(async () => {
    if (!isManager) return;
    try {
      const res = await authAxios.get(`${API_URL}/api/ops/team`);
      setTeamMembers(res.data);
    } catch (err) { logger.error(err); }
  }, [isManager, authAxios]);

  useEffect(() => { if (isManager) fetchTeam(); }, [isManager, fetchTeam]);

  const addTeamMember = async () => {
    if (!newMember.name || !newMember.phone || !newMember.pin) return;
    try {
      await authAxios.post(`${API_URL}/api/ops/team`, { ...newMember, role: "executive" });
      setNewMember({ name: "", phone: "", pin: "" });
      setShowAddMember(false);
      await fetchTeam();
    } catch (err) { alert(err.response?.data?.detail || "Failed to add"); }
  };

  const assignBooking = async (bookingId, opsPhone, opsName) => {
    try {
      await authAxios.put(`${API_URL}/api/ops/bookings/${bookingId}/assign`, { ops_phone: opsPhone, ops_name: opsName });
      setShowAssign(null);
      await fetchBookings();
    } catch (err) { alert("Failed to assign"); }
  };

  const fetchPhotos = async (bookingId) => {
    try {
      const res = await authAxios.get(`${API_URL}/api/ops/bookings/${bookingId}/photos`);
      setPhotos(res.data);
    } catch (err) { logger.error(err); }
  };

  const getLocationInternal = getLocation; // alias for clarity; used inside updateStatus below

  const updateStatus = async (bookingId, status) => {
    setUpdating(true);
    setGeoError("");
    const loc = await getLocationInternal();
    try {
      await authAxios.post(`${API_URL}/api/ops/bookings/${bookingId}/status`, {
        status,
        latitude: loc?.latitude || null,
        longitude: loc?.longitude || null
      });
      await fetchBookings();
      if (selectedBooking) setSelectedBooking(bookings.find(b => b.id === bookingId) || selectedBooking);
    } catch (err) { alert("Failed to update status"); }
    setUpdating(false);
  };

  const uploadPhoto = async (bookingId, photoType) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.capture = "environment";
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploading(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("photo_type", photoType);
      try {
        await authAxios.post(`${API_URL}/api/ops/bookings/${bookingId}/photos`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        await fetchPhotos(bookingId);
      } catch (err) { alert("Upload failed"); }
      setUploading(false);
    };
    input.click();
  };

  const today = new Date().toISOString().split("T")[0];
  // Only paid bookings count as real jobs. Ops shouldn't be prepping for
  // setups the customer hasn't paid for — those are leads, handled elsewhere.
  const isPaid = (b) => (b.payment_status || "").toLowerCase() === "paid";
  const todayBookings = useMemo(() => bookings.filter(b => b.date === today && isPaid(b)), [bookings, today]);
  const upcomingBookings = useMemo(() => bookings.filter(b => b.date > today && isPaid(b)), [bookings, today]);
  const pastBookings = useMemo(() => bookings.filter(b => b.date < today && isPaid(b)), [bookings, today]);
  const teamExecutives = useMemo(() => teamMembers.filter(m => m.role === "executive"), [teamMembers]);

  const getStatusStep = (report) => {
    if (!report) return 0;
    if (report.ops_completed_at) return 3;
    if (report.ops_setup_started_at) return 2;
    if (report.ops_arrived_at) return 1;
    return 0;
  };

  // Booking detail view
  if (selectedBooking) {
    const report = selectedBooking.ops_report;
    const step = getStatusStep(report);

    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        <div className="bg-gradient-to-r from-[#1E1B4B] to-[#312e81] px-4 py-4 sticky top-0 z-10">
          <button onClick={() => setSelectedBooking(null)} className="flex items-center gap-2 text-white/80 text-sm mb-2" data-testid="ops-back-btn">
            <ArrowLeft size={16} /> Back
          </button>
          <h2 className="text-white font-bold text-lg">{selectedBooking.id}</h2>
          <p className="text-white/60 text-xs">{selectedBooking.date} | {selectedBooking.package}</p>
        </div>

        <div className="p-4 space-y-4">
          {/* Customer Info */}
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <h3 className="font-bold text-[#1E1B4B] mb-2">{selectedBooking.customer_name}</h3>
            <p className="text-sm text-[#475569] flex items-center gap-2"><Phone size={14} /> {selectedBooking.customer_phone}</p>
            <p className="text-sm text-[#475569] flex items-center gap-2 mt-1"><MapPin size={14} /> {selectedBooking.address}</p>
            {selectedBooking.notes && <p className="text-sm text-[#94a3b8] mt-2 italic">"{selectedBooking.notes}"</p>}
          </div>

          {/* Status Timeline */}
          <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="ops-status-timeline">
            <h3 className="font-bold text-[#1E1B4B] mb-4">Setup Progress</h3>
            <div className="space-y-3">
              {[
                { key: "arrived", label: "Arrived at Venue", step: 1 },
                { key: "setup_started", label: "Setup Started", step: 2 },
                { key: "completed", label: "Setup Completed", step: 3 },
              ].map(({ key, label, step: s }) => (
                <div key={key} className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${step >= s ? 'bg-[#25D366] text-white' : 'bg-gray-100 text-gray-400'}`}>
                    {step >= s ? <Check size={16} /> : <Clock size={16} />}
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${step >= s ? 'text-[#1E1B4B]' : 'text-[#94a3b8]'}`}>{label}</p>
                    {report && report[`ops_${key}_at`] && (
                      <p className="text-xs text-[#94a3b8]">{new Date(report[`ops_${key}_at`]).toLocaleTimeString()}
                        {report[`ops_${key}_location`] && <span className="ml-1 text-[#25D366]">(GPS tagged)</span>}
                      </p>
                    )}
                  </div>
                  {step === s - 1 && (
                    <button onClick={() => updateStatus(selectedBooking.id, key)} disabled={updating}
                      className="px-3 py-1.5 bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white rounded-lg text-xs font-bold flex items-center gap-1 disabled:opacity-50"
                      data-testid={`ops-btn-${key}`}>
                      {updating ? <Loader2 size={12} className="animate-spin" /> : <MapPin size={12} />}
                      {s === 1 ? "Check In" : s === 2 ? "Start" : "Complete"}
                    </button>
                  )}
                </div>
              ))}
            </div>
            {geoError && <p className="text-xs text-orange-500 mt-2">{geoError} (status still updated)</p>}
          </div>

          {/* Photos */}
          <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="ops-photos-section">
            <h3 className="font-bold text-[#1E1B4B] mb-3">Photos</h3>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <button onClick={() => uploadPhoto(selectedBooking.id, "before")} disabled={uploading}
                className="py-8 rounded-xl border-2 border-dashed border-[#E83E8C]/30 text-[#E83E8C] flex flex-col items-center gap-2 hover:bg-[#E83E8C]/5 transition-all disabled:opacity-50"
                data-testid="ops-upload-before">
                {uploading ? <Loader2 size={24} className="animate-spin" /> : <Camera size={24} />}
                <span className="text-xs font-medium">Before Setup</span>
              </button>
              <button onClick={() => uploadPhoto(selectedBooking.id, "after")} disabled={uploading}
                className="py-8 rounded-xl border-2 border-dashed border-[#25D366]/30 text-[#25D366] flex flex-col items-center gap-2 hover:bg-[#25D366]/5 transition-all disabled:opacity-50"
                data-testid="ops-upload-after">
                {uploading ? <Loader2 size={24} className="animate-spin" /> : <Camera size={24} />}
                <span className="text-xs font-medium">After Setup</span>
              </button>
            </div>
            {photos.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {photos.map((p) => (
                  <div key={p.storage_path || p.photo_type + p.uploaded_at} className="relative">
                    <img src={`${API_URL}/api/files/${p.storage_path}`} alt={p.photo_type} className="w-full h-24 object-cover rounded-lg" />
                    <span className={`absolute top-1 left-1 px-1.5 py-0.5 rounded text-[10px] font-bold text-white ${p.photo_type === 'before' ? 'bg-[#E83E8C]' : 'bg-[#25D366]'}`}>{p.photo_type}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Bookings list view
  return (
    <div className="min-h-screen bg-gray-50 pb-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1E1B4B] to-[#312e81] px-4 py-5 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_party-in-2hrs/artifacts/47ayyz1k_SUMIT%20DOOZYGO%20LOGO%20-%20AUG%202023.png" alt="DoozyGo" className="h-10" />
            <div>
              <h1 className="text-white font-bold text-lg font-['Outfit']">Hi, {auth.name}</h1>
              <p className="text-white/60 text-xs">{isManager ? "Ops Manager" : "Ops Executive"} — {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={trackingActive ? stopTracking : startTracking}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                trackingActive
                  ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                  : 'bg-white/10 text-white/70 hover:text-white hover:bg-white/20'
              }`}
              data-testid="ops-tracking-toggle"
            >
              {trackingActive ? (
                <><Navigation size={14} className="animate-pulse" /> Live</>
              ) : (
                <><Navigation size={14} /> Track</>
              )}
            </button>
            {isManager && (
              <button onClick={() => setShowTeam(!showTeam)} className={`p-2 rounded-lg ${showTeam ? 'bg-white/20 text-white' : 'text-white/60 hover:text-white'}`} data-testid="ops-team-toggle">
                <Users size={20} />
              </button>
            )}
            <button onClick={() => setShowTraining(true)} className="p-2 text-white/60 hover:text-white" data-testid="ops-training-btn" title="Training">
              <GraduationCap size={20} />
            </button>
            <button onClick={onLogout} className="p-2 text-white/60 hover:text-white" data-testid="ops-logout-btn">
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* My Attendance — self-view for field staff */}
        <OpsAttendanceCard authAxios={authAxios} />

        {/* Tracking Status Bar */}
        {trackingActive && (
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3 flex items-center justify-between" data-testid="ops-tracking-bar">
            <div className="flex items-center gap-2">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
              </span>
              <span className="text-xs text-emerald-700 font-medium">Location sharing active</span>
            </div>
            <div className="flex gap-1.5">
              {["en_route", "on_site", "completed"].map(s => (
                <button
                  key={s}
                  onClick={() => setTrackingStatus(s)}
                  className={`px-2 py-1 rounded-md text-[10px] font-medium transition-all ${
                    trackingStatus === s ? 'bg-emerald-500 text-white' : 'bg-white text-[#475569] border border-gray-200'
                  }`}
                  data-testid={`ops-track-status-${s}`}
                >
                  {s === "en_route" ? "En Route" : s === "on_site" ? "On Site" : "Done"}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Ops Manager: Team Panel */}
        {isManager && showTeam && (
          <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="ops-team-panel">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-[#1E1B4B] text-sm">Team Members</h3>
              <button onClick={() => setShowAddMember(!showAddMember)} className="px-3 py-1 bg-[#E83E8C] text-white rounded-lg text-xs font-medium">Add</button>
            </div>
            {showAddMember && (
              <div className="flex gap-2 mb-3">
                <input value={newMember.name} onChange={e => setNewMember({...newMember, name: e.target.value})} placeholder="Name" className="flex-1 px-2 py-1.5 rounded-lg border text-xs" />
                <input value={newMember.phone} onChange={e => setNewMember({...newMember, phone: e.target.value})} placeholder="Phone" className="flex-1 px-2 py-1.5 rounded-lg border text-xs" />
                <input value={newMember.pin} onChange={e => setNewMember({...newMember, pin: e.target.value})} placeholder="PIN" maxLength={4} type="password" className="w-16 px-2 py-1.5 rounded-lg border text-xs" />
                <button onClick={addTeamMember} className="px-3 py-1.5 bg-[#25D366] text-white rounded-lg text-xs">Save</button>
              </div>
            )}
            <div className="space-y-1">
              {teamMembers.map((m) => (
                <div key={m.phone || m.name} className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 text-xs">
                  <div className="w-6 h-6 rounded-full bg-[#1E1B4B] flex items-center justify-center text-white text-[10px] font-bold">{m.name[0]}</div>
                  <span className="font-medium text-[#1E1B4B]">{m.name}</span>
                  <span className="text-[#94a3b8]">{m.phone}</span>
                  <span className={`ml-auto px-1.5 py-0.5 rounded text-[10px] font-medium ${m.role === 'manager' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'}`}>{m.role}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12"><Loader2 className="animate-spin mx-auto text-[#E83E8C]" size={32} /></div>
        ) : bookings.length === 0 ? (
          <div className="text-center py-16">
            <MapPin size={48} className="mx-auto text-[#94a3b8] mb-4" />
            <p className="text-[#475569] font-medium">No bookings assigned</p>
            <p className="text-sm text-[#94a3b8] mt-1">Check with admin for assignments</p>
          </div>
        ) : (
          <>
            {/* Today's Bookings */}
            {todayBookings.length > 0 && (
              <div>
                <h2 className="text-sm font-bold text-[#E83E8C] uppercase tracking-wider mb-2">Today ({todayBookings.length})</h2>
                {todayBookings.map((b, i) => (
                  <BookingCard key={b.id || i} booking={b} onSelect={() => { setSelectedBooking(b); fetchPhotos(b.id); }} isManager={isManager} onAssign={isManager ? (b2) => setShowAssign(b2) : null} />
                ))}
              </div>
            )}

            {/* Upcoming */}
            {upcomingBookings.length > 0 && (
              <div>
                <h2 className="text-sm font-bold text-[#FF9F1C] uppercase tracking-wider mb-2">Upcoming ({upcomingBookings.length})</h2>
                {upcomingBookings.map((b, i) => (
                  <BookingCard key={b.id || i} booking={b} onSelect={() => { setSelectedBooking(b); fetchPhotos(b.id); }} isManager={isManager} onAssign={isManager ? (b2) => setShowAssign(b2) : null} />
                ))}
              </div>
            )}

            {/* Past */}
            {pastBookings.length > 0 && (
              <div>
                <h2 className="text-sm font-bold text-[#94a3b8] uppercase tracking-wider mb-2">Past ({pastBookings.length})</h2>
                {pastBookings.slice(0, 10).map((b, i) => (
                  <BookingCard key={b.id || i} booking={b} onSelect={() => { setSelectedBooking(b); fetchPhotos(b.id); }} isManager={isManager} onAssign={isManager ? (b2) => setShowAssign(b2) : null} />
                ))}
              </div>
            )}
          </>
        )}

        {/* Assign Modal */}
        {showAssign && (
          <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50" onClick={() => setShowAssign(null)}>
            <div className="bg-white rounded-t-3xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="ops-assign-modal">
              <h3 className="font-bold text-[#1E1B4B] mb-3">Assign to {showAssign.customer_name}</h3>
              <div className="space-y-2">
                {teamExecutives.map((m, i) => (
                  <button key={m.phone || i} onClick={() => assignBooking(showAssign.id, m.phone, m.name)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl border hover:bg-[#FFFAF5] transition-all text-left">
                    <div className="w-8 h-8 rounded-full bg-[#1E1B4B] flex items-center justify-center text-white font-bold text-xs">{m.name[0]}</div>
                    <div><p className="font-semibold text-sm">{m.name}</p><p className="text-xs text-[#94a3b8]">{m.phone}</p></div>
                  </button>
                ))}
              </div>
              <button onClick={() => setShowAssign(null)} className="mt-4 w-full py-2 bg-gray-100 rounded-xl text-sm text-gray-500">Cancel</button>
            </div>
          </div>
        )}

        {/* Training Drawer */}
        {showTraining && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center" onClick={() => setShowTraining(false)}>
            <div className="bg-[#FFFAF5] rounded-t-3xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-5" onClick={e => e.stopPropagation()} data-testid="ops-training-drawer">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-bold text-[#1E1B4B] font-['Outfit'] flex items-center gap-2"><GraduationCap size={20} className="text-[#E83E8C]"/> My Training</h3>
                <button onClick={() => setShowTraining(false)} className="text-gray-400 text-2xl leading-none" data-testid="ops-training-close">×</button>
              </div>
              <TrainingTab authAxios={authAxios} />
            </div>
          </div>
        )}
      </div>

      {/* DoozyBot Training Assistant — floats bottom-left, available to all ops staff */}
      <StaffAssistantWidget
        authAxios={authAxios}
        roleLabel={isManager ? "Ops Manager" : "Ops Executive"}
      />
      {/* Internal Team Chat — floats bottom-right; uses the ops-scoped endpoints */}
      <InternalChatWidget authAxios={authAxios} basePath="/api/ops" />
      {/* Policy re-sign banner */}
      <PolicyResignBanner authAxios={authAxios} employeeName={auth?.name || ""} />
    </div>
  );
};

export default OpsPortal;
